import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

export type UserTier = "free" | "pro";

// サインアップ時の配信同意をログイン確立まで一時保持するキー（確認コード入力前はAPIを呼べないため）
export const PENDING_CONSENT_KEY = "kakumon_pending_marketing_consent";

interface UserProfile {
  tier: UserTier;
  examCategory: string;
  onboardingCompleted: boolean;
  marketingConsent: boolean | null; // null=未確認（バナーで確認する）
}

interface UserProfileContextType {
  profile: UserProfile;
  userTier: UserTier;
  /** 機能ゲート。2026年度は無料開放のため全員 true */
  isPro: boolean;
  /** 無料開放によって Pro 機能が開いている状態（＝有料プラン加入ではない） */
  isFreeOpenPeriod: boolean;
  isLoading: boolean;
  isLoaded: boolean;
  updateProfile: (updates: Partial<Pick<UserProfile, "examCategory" | "onboardingCompleted" | "marketingConsent">>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const DEFAULT_PROFILE: UserProfile = {
  tier: "free",
  examCategory: "SC",
  onboardingCompleted: false,
  marketingConsent: null,
};

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<UserProfile>(DEFAULT_PROFILE);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const fetchProfile = useCallback(async () => {
    if (!user?.idToken) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/user-profile`, {
        headers: { Authorization: `Bearer ${user.idToken}` },
      });
      if (res.data?.status === "success") {
        setProfile({
          tier: res.data.data.tier || "free",
          examCategory: res.data.data.examCategory || "SC",
          onboardingCompleted: res.data.data.onboardingCompleted || false,
          marketingConsent: res.data.data.marketingConsent ?? null,
        });
      }
    } catch {
      // API未デプロイ等の場合はデフォルトのまま
    } finally {
      setIsLoading(false);
      setIsLoaded(true);
    }
  }, [user?.idToken]);

  // ログイン後にプロファイル取得（usersテーブル自動作成もトリガー）
  useEffect(() => {
    if (!isAuthenticated || !user?.idToken) {
      setProfile(DEFAULT_PROFILE);
      setIsLoaded(false);
      return;
    }
    fetchProfile();
  }, [isAuthenticated, user?.idToken, fetchProfile]);

  const updateProfile = useCallback(async (updates: Partial<Pick<UserProfile, "examCategory" | "onboardingCompleted" | "marketingConsent">>) => {
    if (!user?.idToken) return;
    try {
      const res = await axios.put(`${API_URL}/user-profile`, updates, {
        headers: { Authorization: `Bearer ${user.idToken}` },
      });
      if (res.data?.status === "success") {
        setProfile({
          tier: res.data.data.tier || "free",
          examCategory: res.data.data.examCategory || "SC",
          onboardingCompleted: res.data.data.onboardingCompleted || false,
          marketingConsent: res.data.data.marketingConsent ?? null,
        });
      }
    } catch {
      // silent fail
    }
  }, [user?.idToken]);

  // サインアップ時に一時保持した配信同意をログイン確立後に保存
  // （失敗時は同意NULLのままなのでバナーで再確認される）
  useEffect(() => {
    if (!isAuthenticated || !user?.idToken) return;
    const pending = localStorage.getItem(PENDING_CONSENT_KEY);
    if (pending === null) return;
    localStorage.removeItem(PENDING_CONSENT_KEY);
    updateProfile({ marketingConsent: pending === "true" });
  }, [isAuthenticated, user?.idToken, updateProfile]);

  // 2026年度は全機能無料開放。Pro有料化時にこの2行を削除して profile.tier に戻す
  // （isPro は「機能が開いているか」であって「課金しているか」ではない。プラン表示は
  //   isFreeOpenPeriod を見て出し分ける — 無料の利用者に有料プラン表示を出さないため）
  const effectiveTier: UserTier = "pro";
  const isFreeOpenPeriod = true;

  return (
    <UserProfileContext.Provider
      value={{
        profile,
        userTier: effectiveTier,
        isPro: true,
        isFreeOpenPeriod,
        isLoading,
        isLoaded,
        updateProfile,
        refreshProfile: fetchProfile,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

export function useUserProfile(): UserProfileContextType {
  const ctx = useContext(UserProfileContext);
  if (!ctx) throw new Error("useUserProfile must be used within UserProfileProvider");
  return ctx;
}
